import moment from 'moment';
// usage examples:
//
// import {getScrollTop} from './lib/utilities';
// or
// import * as Utils from './lib/utilities';

/**
 * Detect breakpoints - see https://adactio.com/journal/5429 for more info on usage
 * @return {String} - the value of breakpoint set in the CSS
 */
export function getBreakpoint() {
  return window
    .getComputedStyle(document.body, ':after')
    .getPropertyValue('content');
}

/**
 * Get scroll position
 * @return {Undefined}
 */
export function getScrollTop() {
  const doc = document.documentElement;
  return (window.pageYOffset || doc.scrollTop) - (doc.clientTop || 0);
}

/**
 * Loads data stored within a DOM element using the `bootstrap` namespace
 * @param  {string} id
 * @return {(Object|undefined)}
 */
export function bootstrapData(id) {
  const el = document.getElementById(`bootstrap-${id}`);

  if (el == null) return {};

  const escapedData = el.innerHTML;
  const div = document.createElement('div');

  div.innerHTML = escapedData;
  return JSON.parse(div.innerText);
}

class RequestError extends Error {
  constructor(response, text, ...params) {
    super(...params);

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, RequestError);
    }

    const {status, statusText} = response;

    try {
      this.json = JSON.parse(text);
    } catch (e) {
      this.json = {errors: [{message: 'Oops! Something went wrong'}]};
    }

    this.status = status;
    this.statusText = statusText;
    this.message = `${status} - ${statusText}\n\n${text}`;
  }
}

/**
 * Raise an error if the provided Response object is not in
 * the "ok" range (200-299). Otherwise, it returns the object.
 * @param  {Obj} response Response object
 * @return {Obj}
 */
export function raiseForStatus(response) {
  if (response.ok) {
    if (response.status === 204) return response;

    return response.text().then((text) => {
      try {
        return JSON.parse(text);
      } catch (e) {
        throw new Error(`Body could not be parsed as JSON: ${text}`);
      }
    });
  }
  return response.text().then((text) => {
    throw new RequestError(response, text);
  });
}

/**
 * Based on https://davidwalsh.name/javascript-polling
 *
 * @param   {Promise}   promise  Promise to poll for resolution
 * @param   {Function}  compare  A method which should return a Boolean to stop or continue polling
 * @param   {number}    wait     Milliseconds between calls
 *
 * @return  {Promise}
 */
export function poll({promise, compare, wait = 1000, debug, globalVarName = null} = {}) {
  const checkStatus = async (resolve, reject) => {
    let result;

    try {
      result = await promise();
    } catch (e) {
      reject(e);
    }
    if (debug) console.log(result, compare(result));
    if (result != null && compare(result)) resolve(result);
    else {
      // this allows us to clear the interval from other parts of the app
      if (globalVarName) window[globalVarName] = setTimeout(checkStatus, wait, resolve, reject);
      else setTimeout(checkStatus, wait, resolve, reject);
    }
  };

  return new Promise(checkStatus);
}

/**
 * Based on https://davidwalsh.name/javascript-polling
 *
 * @return {String}
 */
export function firebaseTimeFormatter() {
  const today = moment().toISOString();
  return today;
}

/**
 * Check if an object is empty
 *
 * @param {Object} obj
 * @return {bool}
 */
export function isObjectEmpty(obj) {
  return Object.entries(obj).length === 0 && obj.constructor === Object;
}

/**
 * Format sharp hour
 *
 * @param {String} string
 * @return {Boolean}
*/
export function formatSharpHour(string) {
  return string.replace(':00', '');
}

/**
 * Format string into slug
 *
 * @param {String} string
 * @return {String}
 */
export function slugify(string) {
  string = string.replace(/^\s+|\s+$/g, '');
  string = string.toLowerCase();

  const from = 'àáäâèéëêìíïîòóöôùúüûñç·/_,:;';
  const to = 'aaaaeeeeiiiioooouuuunc------';
  for (let i=0, l=from.length; i<l; i++) {
    string = string.replace(
      new RegExp(from.charAt(i), 'g'),
      to.charAt(i)
    );
  }

  string = string
    .replace(/[^a-z0-9 -]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');

  return string;
}
